import { Code, X } from '@phosphor-icons/react';
import { useState, useEffect, useCallback } from 'react';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { MultiSelectBox } from 'src/components/MultiSelectBox';
import { SelectBox } from 'src/components/SelectBox';
import { EquivalentCode } from 'src/components/EquivalentCode';
import { WebhookPayload } from 'src/interfaces/webhook.interface';
import { isValidHttpUrl } from 'src/utils/url';
import { providerDisplayNames } from 'src/utils/provider';
import { AccountConnection } from 'src/interfaces/accountConnection.interface';
import { getFirstAndLastFourUUID } from 'src/utils/number';
import {
  AutoCompleteSelectBox,
  Option,
} from 'src/components/AutoCompleteSelectBox';
import { Loading } from 'src/components/Loading';
import { providerOptions } from 'src/pages/AccountConnections/providerOptions';
import useAuthedRequest from 'src/hooks/useAuthedRequest';

export const subscriptionOptions = [
  { value: 'event_action', label: 'Action' },
  { value: 'event_error', label: 'Error' },
  { value: 'event_human_detection', label: 'Human Detection' },
  { value: 'event_message', label: 'Message' },
  { value: 'event_phone_call_connected', label: 'Phone Call Connected' },
  {
    value: 'event_phone_call_did_not_connect',
    label: 'Phone Call Did Not Connect',
  },
  { value: 'event_phone_call_ended', label: 'Phone Call Ended' },
  {
    value: 'event_call_record_finalized',
    label: 'Post-Call Processing Completed',
  },
  { value: 'event_recording', label: 'Recording' },
  { value: 'event_transcript', label: 'Transcript' },
];

const methods = [{ value: 'POST', label: 'POST' }];

interface WebhookFormProps {
  form: WebhookPayload;
  onChange: (form: WebhookPayload) => void;
  loading?: boolean;
  selectedId?: string;
  children?: React.ReactNode;
  showEquivalentCode?: boolean;
  showRequiredFields?: boolean;
}

export const WebhookForm = ({
  form,
  onChange,
  loading = false,
  selectedId,
  children,
  showEquivalentCode = true,
  showRequiredFields = true,
}: WebhookFormProps) => {
  const { get } = useAuthedRequest();
  const [equivalentCodeOpen, setEquivalentCodeOpen] = useState(false);
  const [inputsWithError, setInputsWithError] = useState<string[]>([]);
  const [attachedAccountConnectionOption, setAttachedAccountConnectionOption] =
    useState<Option>();
  const [isLoadingAccountConnection, setIsLoadingAccountConnection] =
    useState<boolean>(false);

  const fetchAccountConnections = useCallback(async () => {
    const data = await get('/account-connections');

    return data.items
      .filter((ac: AccountConnection) =>
        providerOptions.find(
          (providerOption) =>
            providerOption.value === ac.type && providerOption.type === 'Auth',
        ),
      )
      .map((ac: AccountConnection) => ({
        value: ac.id,
        label: `${providerDisplayNames[ac.type]} - ${getFirstAndLastFourUUID(ac.id || '')}`,
      }));
  }, []);

  const fetchAccountConnectionById = useCallback(async (id: string) => {
    setIsLoadingAccountConnection(true);
    const ac = await get(`/account-connections/${id}`);

    setAttachedAccountConnectionOption({
      value: ac.id,
      label: `${providerDisplayNames[ac.type]} - ${getFirstAndLastFourUUID(ac.id || '')}`,
    });
    setIsLoadingAccountConnection(false);
  }, []);

  useEffect(() => {
    if (form?.method === 'GET') {
      onChange({
        ...form,
        subscriptions: form.subscriptions.filter(
          (item) =>
            item === 'event_phone_call_connected' ||
            item === 'event_phone_call_ended',
        ),
      });
    }
  }, [form?.method]);

  useEffect(() => {
    if (!form.account_connection_id) {
      setAttachedAccountConnectionOption(undefined);
      return;
    }
    fetchAccountConnectionById(form.account_connection_id);
  }, [form?.account_connection_id]);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isValidHttpUrl(e.target.value)) {
      setInputsWithError([...inputsWithError, 'url']);
      onChange({ ...form, url: '' });
    } else {
      setInputsWithError(inputsWithError.filter((item) => item !== 'url'));
      onChange({ ...form, url: e.target.value });
    }
  };

  const handleEndpointWebhook = () => {
    if (selectedId) {
      return `webhooks/update?id=${selectedId}`;
    } else {
      return 'webhooks/create';
    }
  };

  if (isLoadingAccountConnection) {
    return <Loading />;
  }

  return (
    <>
      {children}

      {showEquivalentCode && (
        <div className="flex justify-end mb-1">
          <Button
            onClick={() => setEquivalentCodeOpen(!equivalentCodeOpen)}
            className="flex items-center justify-center"
          >
            <Code className="mr-2" size={20} />
            Show equivalent code
          </Button>
        </div>
      )}

      {showRequiredFields && (
        <div className="flex justify-end italic text-sm mt-4">
          * Required fields
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="mb-6">
          <Label htmlFor="label">Label</Label>
          <Input
            type="text"
            id="label"
            placeholder="Type the label for your webhook..."
            value={form?.label ?? ''}
            onChange={(e) => onChange({ ...form, label: e.target.value })}
            disabled={loading}
            error={
              inputsWithError.includes('label') ? 'Please enter a label' : ''
            }
          />
        </div>

        <div className="mb-6">
          <Label required htmlFor="subscriptions">
            Subscriptions
          </Label>
          <MultiSelectBox
            key={form?.subscriptions?.join('-')}
            options={subscriptionOptions.filter((item) =>
              form?.method === 'GET'
                ? item.value === 'event_phone_call_connected' ||
                  item.value === 'event_phone_call_ended'
                : item,
            )}
            defaultValue={subscriptionOptions.filter((item) =>
              form?.subscriptions?.includes(item.value),
            )}
            variant="outlined"
            color="primary"
            size="medium"
            className="min-w-[140px] bg-neutral-0"
            onChange={(value) => onChange({ ...form, subscriptions: value })}
            disabled={loading}
          />
        </div>

        <div className="mb-6">
          <Label required htmlFor="url">
            URL
          </Label>
          <Input
            type="text"
            id="url"
            placeholder="https://example.com"
            value={form?.url ?? ''}
            onChange={(e) => onChange({ ...form, url: e.target.value })}
            onBlur={handleUrlChange}
            disabled={loading}
            error={
              inputsWithError.includes('url') ? 'Please enter a valid URL' : ''
            }
          />
        </div>

        <div className="mb-6">
          <Label htmlFor="account-connection">Account Connection</Label>

          <AutoCompleteSelectBox
            key={attachedAccountConnectionOption?.value}
            defaultValue={attachedAccountConnectionOption}
            fetchOptions={fetchAccountConnections}
            onChange={(value) =>
              onChange({ ...form, account_connection_id: value })
            }
            variant="outlined"
            color="primary"
            size="medium"
            disabled={loading}
          />
        </div>

        <div className="mb-6">
          <Label htmlFor="method">Method</Label>
          {form?.method === 'GET' ? (
            <>
              <span className="text-neutral-600 text-sm font-bold block mb-4">
                Current: {form?.method}
              </span>
              <span className="text-neutral-600 text-xs font-bold block">
                Note: GET requests are no longer supported as they were
                previously. You can edit this webhook, but you will not be able
                to create new ones using the GET method.
              </span>
            </>
          ) : (
            <SelectBox
              key={form?.method}
              options={methods}
              variant="outlined"
              color="primary"
              size="medium"
              onChange={(value) =>
                onChange({ ...form, method: value as string })
              }
              className="min-w-[140px] bg-neutral-0"
              defaultValue={
                methods.find((item) => item.value === form?.method) ||
                methods[0]
              }
              disabled={loading}
            />
          )}
        </div>
      </div>

      {equivalentCodeOpen && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3>Equivalent Code</h3>
            <X
              className="cursor-pointer inline-block w-5 h-5"
              onClick={() => setEquivalentCodeOpen(false)}
            />
          </div>
          <EquivalentCode
            payload={form}
            endpoint={handleEndpointWebhook()}
            method="POST"
          />
        </div>
      )}
    </>
  );
};
